import { defineStore } from 'pinia'
import { DEFAULT_LOADING_FIGURE, normalizeLoadingFigure, type LoadingFigureSetting } from '../constants/loadingFigures'
import { computed, nextTick, ref, watch, type Ref, type WritableComputedRef } from 'vue'
import { localHLC, repairStoredHlcs } from '../services/hlc'
import { syncEngine } from '../services/syncEngine'
import { DEFAULT_THEME_SETTINGS, type ThemeSettings } from '../theme/presets'
import { normalizeThemeSettings } from '../services/themeEngine'
import { defaultPageNamingSchemes, type PageNamingScheme } from '../services/pageNaming'
import {
  cloneDocumentTemplate,
  createBlankDocumentTemplate,
  defaultDocumentTemplates,
  normalizeDocumentTemplate,
  normalizeDocumentTemplates,
  type DocumentTemplate,
} from '../services/documentTemplates'
import { DEFAULT_PAGE_STATUSES, normalizePageStatuses, type PageStatus } from '../services/pageStatus'
import { defaultPasses, normalizePasses, type RevisionPass } from '../utils/revisionPasses'
import { CRUTCH_DEFAULTS } from '../utils/editorModeTuning'
import { normalizeHabitWord } from '../utils/habits'
import { DEFAULT_PAGE_VIEW_DISPLAY, normalizePageViewDisplay, type PageViewDisplay } from '../utils/pageViewDisplay'
import { IS_MOBILE_APP } from '../utils/appVariant'
import { TYPEWRITER_MOTIONS, type TypewriterMotion } from '../utils/typewriterMotion'
import { DEFAULT_GOAL_DAYS, normalizeGoalDays, type GoalDays } from '../utils/goalModel'
import {
  DEFAULT_CALENDAR_PREFS,
  DEFAULT_DOCKET_PREFS,
  DEFAULT_GOAL_PREFS,
  DEFAULT_TOOL_PREFS,
  normalizeCalendarPrefs,
  normalizeDocketPrefs,
  normalizeGoalPrefs,
  normalizeToolPrefs,
  type CalendarPrefs,
  TOOL_COLORS,
  type DocketPrefs,
  type GoalPrefs,
  type ToolColor,
  type ToolPrefs,
} from '../utils/toolSettings'

export type RightEdgeMode = 'none' | 'toolbar' | 'taskbar'
// How the desktop toolbars and their menus look: set on the page, docked to the window edges, or floating.
export type ToolbarStyle = 'guide' | 'carriage' | 'pill'

/**
 * The daily writing habit as the Goals tool first stored it. Goals now keep it
 * as a writing goal (see stores/goals), which migrates it from here once.
 */
export interface WritingHabit {
  enabled: boolean
  /** Net words that make a writing day; 0 means words don't count. */
  dailyWords: number
  /** Active writing minutes that make a writing day; 0 means time doesn't count. */
  dailyMinutes: number
  /** Writing days per week that keep the streak alive. */
  daysPerWeek: number
  /** 0 = Sunday, 1 = Monday. */
  weekStartsOn: 0 | 1
  /** Hour (0–6) at which a new writing day begins, for writers who work past midnight. */
  dayStartHour: number
  /** Finishing an Editor Mode revision pass also makes a writing day. */
  passDays: boolean
}

const DEFAULT_WRITING_HABIT: WritingHabit = {
  enabled: false,
  dailyWords: 500,
  dailyMinutes: 30,
  daysPerWeek: 5,
  weekStartsOn: 1,
  dayStartHour: 0,
  passDays: true,
}

function normalizeWritingHabit(raw: any): WritingHabit {
  const whole = (value: unknown, min: number, max: number, fallback: number) => {
    const n = Math.round(Number(value))
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
  }
  const d = DEFAULT_WRITING_HABIT
  const habit: WritingHabit = {
    enabled: raw?.enabled === true,
    dailyWords: whole(raw?.dailyWords, 0, 100_000, d.dailyWords),
    dailyMinutes: whole(raw?.dailyMinutes, 0, 1_440, d.dailyMinutes),
    daysPerWeek: whole(raw?.daysPerWeek, 1, 7, d.daysPerWeek),
    weekStartsOn: raw?.weekStartsOn === 0 ? 0 : 1,
    dayStartHour: whole(raw?.dayStartHour, 0, 6, d.dayStartHour),
    passDays: raw?.passDays !== false,
  }
  // A habit needs at least one way to qualify.
  if (habit.dailyWords === 0 && habit.dailyMinutes === 0) habit.dailyWords = d.dailyWords
  return habit
}

/** How loose ends are flagged in the editor and shown while writing. */
export interface LooseEndSettings {
  /** Typing TK as a word flags a loose end. */
  tkEnabled: boolean
  /** More tokens that flag a loose end. A token with a space in it is a pair,
   *  like "[[ ]]", and whatever is typed between becomes the note. */
  customTokens: string[]
  /** Convert typed tokens into loose-end chips, or leave them as plain text and only list them. */
  tokenMode: 'convert' | 'detect'
  /** Shortcut that flags a loose end at the cursor or on the selection. */
  flagShortcut: string
  /** Shortcut that opens the idea capture bar. */
  captureShortcut: string
  /** How loose ends look while focus dimming is on. */
  focusVisibility: 'show' | 'dim' | 'hide'
  /** Offer a kind for each loose end. */
  kindsEnabled: boolean
  kinds: string[]
  /** The kind a new loose end starts with; '' for none. */
  defaultKind: string
  /** Flagging opens the note at once, or drops the loose end and carries on. */
  openNoteOnFlag: boolean
  /** How loose ends look while writing, focus dimming or not. */
  visibility: 'show' | 'dim' | 'hide'
  /** A pin's look: a stamped TK, the letters alone, or a dot. */
  pinStyle: 'stamp' | 'quiet' | 'dot'
  /** Flagged text's look. */
  spanStyle: 'dotted' | 'wavy' | 'highlight' | 'none'
  /** The color of loose ends in the page. */
  color: ToolColor
  /** The page's open loose ends, counted in the bottom bar. */
  bottomBarCount: boolean
  /** How the Loose Ends tool groups its list at first. */
  groupBy: 'page' | 'kind' | 'age'
  /** How much text around a loose end the list shows. */
  excerpt: 'full' | 'short'
  showStats: boolean
  /** Quick Edit: the passage editable in place in the list. */
  quickEdit: boolean
  /** Loose ends this many days old are marked; 0 for never. */
  staleDays: number
  /** The Inbox of parked ideas, at the top of the tool. */
  showIdeas: boolean
  /** Where "Make a note" files a note: under the idea's page, or at the top of the current desk. */
  noteDestination: 'source' | 'desk'
  /** Clearing history asks a second time. */
  confirmClear: boolean
  /** DOCX and ODT exports carry loose ends as margin comments, unless turned off at export. */
  exportComments: boolean
  /** Days and entries of resolved history kept per page (at most 90 and 50). */
  historyDays: number
  historyMax: number
}

export const DEFAULT_LOOSE_END_SETTINGS: LooseEndSettings = {
  tkEnabled: true,
  customTokens: [],
  tokenMode: 'convert',
  flagShortcut: 'Mod+Shift+K',
  captureShortcut: 'Mod+Alt+K',
  focusVisibility: 'show',
  kindsEnabled: false,
  kinds: ['Gap', 'Check', 'Doubt'],
  defaultKind: '',
  openNoteOnFlag: true,
  visibility: 'show',
  pinStyle: 'stamp',
  spanStyle: 'dotted',
  color: 'accent',
  bottomBarCount: false,
  groupBy: 'page',
  excerpt: 'full',
  showStats: true,
  quickEdit: true,
  staleDays: 0,
  showIdeas: true,
  noteDestination: 'source',
  confirmClear: true,
  exportComments: true,
  historyDays: 90,
  historyMax: 50,
}

export function normalizeLooseEndSettings(raw: any): LooseEndSettings {
  const d = DEFAULT_LOOSE_END_SETTINGS
  const strings = (value: unknown, fallback: string[], max: number) => Array.isArray(value)
    ? [...new Set(value.filter((item): item is string => typeof item === 'string').map((item) => item.trim()).filter(Boolean))].slice(0, max)
    : [...fallback]
  const shortcut = (value: unknown, fallback: string) =>
    typeof value === 'string' && /^((Mod|Ctrl|Meta|Alt|Shift)\+)+[^+]+$/.test(value) ? value : fallback
  return {
    tkEnabled: raw?.tkEnabled !== false,
    customTokens: strings(raw?.customTokens, d.customTokens, 12),
    tokenMode: raw?.tokenMode === 'detect' ? 'detect' : 'convert',
    flagShortcut: shortcut(raw?.flagShortcut, d.flagShortcut),
    captureShortcut: shortcut(raw?.captureShortcut, d.captureShortcut),
    focusVisibility: ['show', 'dim', 'hide'].includes(raw?.focusVisibility) ? raw.focusVisibility : d.focusVisibility,
    kindsEnabled: raw?.kindsEnabled === true,
    kinds: strings(raw?.kinds, d.kinds, 12).map((kind) => kind.slice(0, 24)),
    defaultKind: typeof raw?.defaultKind === 'string' ? raw.defaultKind.trim().slice(0, 24) : d.defaultKind,
    openNoteOnFlag: raw?.openNoteOnFlag !== false,
    visibility: oneOf(raw?.visibility, ['show', 'dim', 'hide'] as const, d.visibility),
    pinStyle: oneOf(raw?.pinStyle, ['stamp', 'quiet', 'dot'] as const, d.pinStyle),
    spanStyle: oneOf(raw?.spanStyle, ['dotted', 'wavy', 'highlight', 'none'] as const, d.spanStyle),
    color: oneOf(raw?.color, TOOL_COLORS, d.color),
    bottomBarCount: raw?.bottomBarCount === true,
    groupBy: oneOf(raw?.groupBy, ['page', 'kind', 'age'] as const, d.groupBy),
    excerpt: raw?.excerpt === 'short' ? 'short' : 'full',
    showStats: raw?.showStats !== false,
    quickEdit: raw?.quickEdit !== false,
    staleDays: wholeIn(raw?.staleDays, 0, 365, d.staleDays),
    showIdeas: raw?.showIdeas !== false,
    noteDestination: raw?.noteDestination === 'desk' ? 'desk' : 'source',
    confirmClear: raw?.confirmClear !== false,
    exportComments: raw?.exportComments !== false,
    historyDays: wholeIn(raw?.historyDays, 1, 90, d.historyDays),
    historyMax: wholeIn(raw?.historyMax, 1, 50, d.historyMax),
  }
}

function oneOf<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.includes(value as T) ? value as T : fallback
}

function wholeIn(value: unknown, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value))
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
}

/**
 * Editor Mode: a second way of seeing the page, for revision. It changes the
 * page's look (so the eye stops reading what it remembers) and adds lenses
 * that expose sentence structure.
 */
export type EditorModePalette = 'auto' | 'galley' | 'legal' | 'night' | 'custom'

export interface EditorModeSettings {
  /** 'auto' picks a face unlike the drafting font. */
  font: string
  /** Size relative to the drafting size. */
  fontScale: number
  lineHeight: number
  /** In em. */
  letterSpacing: number
  /** In em. */
  wordSpacing: number
  /** Column width in ch. */
  width: number
  palette: EditorModePalette
  customBg: string
  customText: string
  /** Break the page so each sentence starts on its own line. */
  sentenceLines: boolean
  /** Show each sentence's word count. */
  sentenceCounts: boolean
  /** Mark the first word of every sentence, and overused or repeated ones. */
  openers: boolean
  /** Sentences at least this many words long count as long. */
  longSentence: number
  /** Habit lenses (Free mode; a pass carries its own). */
  echoes: boolean
  filterWords: boolean
  crutchWords: boolean
  dialogueTags: boolean
  paragraphWalls: boolean
  /** The writer's crutch words and phrases. */
  crutchList: string[]
  /** Flag every -ly adverb as a crutch word. */
  crutchLyAdverbs: boolean
  /** Words and phrases no habit lens flags. */
  neverFlag: string[]
  /** Habit lenses that also look inside quotations. */
  habitsInDialogue: { echoes: boolean; filterWords: boolean; crutchWords: boolean }
  /** The revision plan: every pass, in order. */
  passes: RevisionPass[]
}

export const DEFAULT_EDITOR_MODE_SETTINGS: EditorModeSettings = {
  font: 'auto',
  fontScale: 1.1,
  lineHeight: 2,
  letterSpacing: 0.01,
  wordSpacing: 0.1,
  width: 54,
  palette: 'auto',
  customBg: '#ffffff',
  customText: '#111111',
  sentenceLines: false,
  sentenceCounts: false,
  openers: false,
  longSentence: 30,
  echoes: false,
  filterWords: false,
  crutchWords: false,
  dialogueTags: false,
  paragraphWalls: false,
  crutchList: [...CRUTCH_DEFAULTS],
  crutchLyAdverbs: false,
  neverFlag: [],
  habitsInDialogue: { echoes: false, filterWords: false, crutchWords: false },
  passes: defaultPasses(),
}

const HABIT_WORD_LIST_MAX = 300

function habitWordList(raw: unknown, fallback: string[]): string[] {
  if (!Array.isArray(raw)) return [...fallback]
  const seen = new Set<string>()
  for (const item of raw) {
    if (typeof item !== 'string') continue
    const word = normalizeHabitWord(item).slice(0, 40)
    if (word) seen.add(word)
    if (seen.size >= HABIT_WORD_LIST_MAX) break
  }
  return [...seen]
}

export function normalizeEditorModeSettings(raw: any): EditorModeSettings {
  const d = DEFAULT_EDITOR_MODE_SETTINGS
  const num = (value: unknown, min: number, max: number, fallback: number) => {
    const n = Number(value)
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
  }
  const color = (value: unknown, fallback: string) =>
    typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback
  return {
    font: typeof raw?.font === 'string' && raw.font.trim() ? raw.font : d.font,
    fontScale: num(raw?.fontScale, 0.7, 1.8, d.fontScale),
    lineHeight: num(raw?.lineHeight, 1, 3.2, d.lineHeight),
    letterSpacing: num(raw?.letterSpacing, -0.05, 0.2, d.letterSpacing),
    wordSpacing: num(raw?.wordSpacing, -0.1, 0.8, d.wordSpacing),
    width: Math.round(num(raw?.width, 25, 140, d.width)),
    palette: ['auto', 'galley', 'legal', 'night', 'custom'].includes(raw?.palette) ? raw.palette : d.palette,
    customBg: color(raw?.customBg, d.customBg),
    customText: color(raw?.customText, d.customText),
    sentenceLines: raw?.sentenceLines === true,
    sentenceCounts: raw?.sentenceCounts === true,
    openers: raw?.openers === true,
    longSentence: Math.round(num(raw?.longSentence, 10, 80, d.longSentence)),
    echoes: raw?.echoes === true,
    filterWords: raw?.filterWords === true,
    crutchWords: raw?.crutchWords === true,
    dialogueTags: raw?.dialogueTags === true,
    paragraphWalls: raw?.paragraphWalls === true,
    crutchList: habitWordList(raw?.crutchList, CRUTCH_DEFAULTS),
    crutchLyAdverbs: raw?.crutchLyAdverbs === true,
    neverFlag: habitWordList(raw?.neverFlag, []),
    habitsInDialogue: {
      echoes: raw?.habitsInDialogue?.echoes === true,
      filterWords: raw?.habitsInDialogue?.filterWords === true,
      crutchWords: raw?.habitsInDialogue?.crutchWords === true,
    },
    passes: normalizePasses(raw?.passes),
  }
}

/** Which Harper checks run beyond spelling, and how. Everything is off by default. */
export type ProofingDialect = 'American' | 'British' | 'Canadian' | 'Australian' | 'Indian'

export interface ProofingSettings {
  grammar: boolean
  punctuation: boolean
  style: boolean
  dialect: ProofingDialect
  /** Harper rule name → forced on/off. Rules not listed keep Harper's default. */
  ruleOverrides: Record<string, boolean>
}

export const PROOFING_DIALECTS: ProofingDialect[] = ['American', 'British', 'Canadian', 'Australian', 'Indian']

export const DEFAULT_PROOFING_SETTINGS: ProofingSettings = {
  grammar: false,
  punctuation: false,
  style: false,
  dialect: 'American',
  ruleOverrides: {},
}

export function normalizeProofingSettings(raw: any): ProofingSettings {
  const ruleOverrides: Record<string, boolean> = {}
  if (raw?.ruleOverrides && typeof raw.ruleOverrides === 'object') {
    for (const [rule, enabled] of Object.entries(raw.ruleOverrides)) {
      if (typeof enabled === 'boolean') ruleOverrides[rule] = enabled
    }
  }
  return {
    grammar: raw?.grammar === true,
    punctuation: raw?.punctuation === true,
    style: raw?.style === true,
    dialect: PROOFING_DIALECTS.includes(raw?.dialect) ? raw.dialect : 'American',
    ruleOverrides,
  }
}

const STORAGE_KEY = 'ink-settings'
const LOCAL_BACKUP_STORAGE_KEY = 'ink-local-backup-settings'

export interface BackupTier {
  id: string
  label: string
  bucketUnit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months'
  bucketWidth: number
  maxBuckets: number
}

export interface BackupSettings {
  automaticEnabled: boolean
  periodicEnabled: boolean
  periodicMinutes: number
  idleEnabled: boolean
  idleMinutes: number
  onCloseEnabled: boolean
  onLaunchEnabled: boolean
  format: 'zip' | 'tar.gz' | 'folder'
  includeMarkdown: boolean
  destination: string | null
  tiers: BackupTier[]
  maxStorageMb: number | null
}

export const defaultBackupSettings: BackupSettings = {
  automaticEnabled: true,
  periodicEnabled: true,
  periodicMinutes: 30,
  idleEnabled: false,
  idleMinutes: 10,
  onCloseEnabled: true,
  onLaunchEnabled: true,
  format: 'zip',
  includeMarkdown: true,
  destination: null,
  tiers: [
    { id: 'recent', label: 'Recent', bucketUnit: 'hours', bucketWidth: 1, maxBuckets: 24 },
    { id: 'daily', label: 'Daily', bucketUnit: 'days', bucketWidth: 1, maxBuckets: 14 },
    { id: 'monthly', label: 'Monthly', bucketUnit: 'months', bucketWidth: 1, maxBuckets: 12 },
  ],
  maxStorageMb: null,
}

function loadBackupSettings(): BackupSettings {
  try {
    const saved = JSON.parse(localStorage.getItem(LOCAL_BACKUP_STORAGE_KEY) || '{}')
    return {
      ...defaultBackupSettings,
      ...saved,
      tiers: Array.isArray(saved.tiers) ? saved.tiers : structuredClone(defaultBackupSettings.tiers),
    }
  } catch {
    return structuredClone(defaultBackupSettings)
  }
}

export const defaultTypographySettings = {
  // Punctuation
  emDash: true,           // -- → —
  ellipsis: true,         // ... → …
  plusMinus: true,        // +/- → ±
  notEqual: true,         // != → ≠
  // Smart Quotes (paired toggles)
  doubleQuotes: true,     // "word" → “word”
  singleQuotes: true,     // 'word' → ‘word’
  // Off: Backspace deletes a curled quote like any other character.
  // On: the first Backspace after one turns it back into a straight quote.
  backspaceRestoresStraightQuotes: false,
  // Arrows
  leftArrow: true,        // <- → ←
  rightArrow: true,       // -> → →
  // Symbols & Trademarks
  copyright: true,        // (c) → ©
  trademark: true,        // (tm) → ™
  registeredTrademark: true, // (r) → ®
  servicemark: true,      // (sm) → ℠
  // Fractions
  oneHalf: true,          // 1/2 → ½
  oneQuarter: true,       // 1/4 → ¼
  threeQuarters: true,    // 3/4 → ¾
  // Math & Typography
  multiplication: true,   // 2x3 → 2×3
  laquo: true,            // << → «
  raquo: true,            // >> → »
  superscriptTwo: true,   // ^2 → ²
  superscriptThree: true, // ^3 → ³
}

export const defaultToolbarSettings = {
  top: {
    panelToggle: true,
    undo: true,
    redo: true,
    blockType: true,
    fontFamily: true,
    fontSize: true,
    textColor: true,
    highlightColor: true,
    bold: true,
    italic: true,
    underline: true,
    strike: true,
    subscript: true,
    superscript: true,
    code: true,
    clearFormatting: true,
    alignLeft: true,
    alignCenter: true,
    alignRight: true,
    alignJustify: true,
    bulletList: true,
    orderedList: true,
    taskList: true,
    horizontalRule: true,
    link: true,
    image: true,
    table: true,
    details: true,
    looseEnd: true,
    dragHandle: true,
    history: true,
    wordCount: false,
    fileMenu: true,
    windowControls: true,
  },
  bottom: {
    docName: true,
    fontPicker: true,
    colorPickers: true,
    widthSlider: true,
    editorMode: true,
    notesView: true,
    pageView: true,
    saveStatus: true,
    wordCount: false,
    account: true,
  },
}

export const defaultIconStyles: Record<string, { size: number; strokeWidth: number }> = {
  undo: { size: 14, strokeWidth: 1.4 },
  redo: { size: 14, strokeWidth: 1.4 },
  paragraph: { size: 14, strokeWidth: 1.75 },
  heading1: { size: 14, strokeWidth: 1.75 },
  heading2: { size: 14, strokeWidth: 1.75 },
  heading3: { size: 14, strokeWidth: 1.75 },
  heading4: { size: 14, strokeWidth: 1.75 },
  heading5: { size: 14, strokeWidth: 1.75 },
  heading6: { size: 14, strokeWidth: 1.75 },
  blockquote: { size: 14, strokeWidth: 1.75 },
  codeBlock: { size: 14, strokeWidth: 1.75 },
  font: { size: 13, strokeWidth: 1.4 },
  fontSize: { size: 14, strokeWidth: 1.75 },
  fontColor: { size: 14, strokeWidth: 1.4 },
  highlightColor: { size: 14, strokeWidth: 1.4 },
  bold: { size: 13, strokeWidth: 1.75 },
  italic: { size: 13, strokeWidth: 1.4 },
  underline: { size: 13, strokeWidth: 1.4 },
  strikethrough: { size: 14, strokeWidth: 1.4 },
  subscript: { size: 14, strokeWidth: 1.75 },
  superscript: { size: 14, strokeWidth: 1.75 },
  inlineCode: { size: 14, strokeWidth: 1.4 },
  alignLeft: { size: 14, strokeWidth: 1.5 },
  alignCenter: { size: 14, strokeWidth: 1.5 },
  alignRight: { size: 14, strokeWidth: 1.5 },
  justify: { size: 14, strokeWidth: 1.5 },
  bulletList: { size: 14, strokeWidth: 1.5 },
  orderedList: { size: 14, strokeWidth: 1.5 },
  taskList: { size: 14, strokeWidth: 1.4 },
  horizontalRule: { size: 14, strokeWidth: 1.5 },
  link: { size: 14, strokeWidth: 1.4 },
  image: { size: 14, strokeWidth: 1.4 },
  table: { size: 14, strokeWidth: 1.4 },
  details: { size: 14, strokeWidth: 1.4 },
  dragHandle: { size: 14, strokeWidth: 1.4 },
  versionHistory: { size: 14, strokeWidth: 1.4 },
  save: { size: 13, strokeWidth: 1.3 },
  exportClipboard: { size: 13, strokeWidth: 1.3 },
  exportFormatted: { size: 13, strokeWidth: 1.3 },
  exportMarkdown: { size: 13, strokeWidth: 1.3 },
  publishPost: { size: 13, strokeWidth: 1.3 },
  exportODT: { size: 13, strokeWidth: 1.2 },
  exportDOCX: { size: 13, strokeWidth: 1.2 },
  exportPDF: { size: 13, strokeWidth: 1.2 },
  exportEPUB: { size: 13, strokeWidth: 1.2 },
  exportTXT: { size: 13, strokeWidth: 1.2 },
  exportJSON: { size: 13, strokeWidth: 1.2 },
  importDocument: { size: 13, strokeWidth: 1.25 },
  settings: { size: 13, strokeWidth: 1.3 },
  minimizeWindow: { size: 12, strokeWidth: 1.8 },
  maximizeWindow: { size: 12, strokeWidth: 1.5 },
  closeWindow: { size: 10, strokeWidth: 1.5 },
  createDocument: { size: 11, strokeWidth: 1.3 },
  document: { size: 13, strokeWidth: 1.2 },
  createFolder: { size: 11, strokeWidth: 1.2 },
  folder: { size: 13, strokeWidth: 1.1 },
  createDesk: { size: 11, strokeWidth: 1.2 },
  desk: { size: 13, strokeWidth: 1.2 },
  pinPanel: { size: 13, strokeWidth: 1.3 },
  search: { size: 12, strokeWidth: 1.3 },
  cut: { size: 14, strokeWidth: 1.4 },
  copy: { size: 14, strokeWidth: 1.4 },
  paste: { size: 14, strokeWidth: 1.4 },
  selectAll: { size: 14, strokeWidth: 1.4 },
  looseEnd: { size: 13, strokeWidth: 1.4 },
  parkIdea: { size: 13, strokeWidth: 1.4 },
  jumpTop: { size: 14, strokeWidth: 1.4 },
  jumpBottom: { size: 14, strokeWidth: 1.4 },
}

export const contextMenuItemCatalog: Record<string, { key: string; label: string; icon: string }[]> = {
  spellcheck: [
    { key: 'ignoreOnce', label: 'Ignore Once', icon: 'closeWindow' },
    { key: 'disableRule', label: 'Disable This Rule', icon: 'closeWindow' },
  ],
  link: [
    { key: 'openLink', label: 'Open Link', icon: 'link' },
    { key: 'editLink', label: 'Edit Link...', icon: 'link' },
    { key: 'copyLinkAddress', label: 'Copy Link Address', icon: 'copy' },
    { key: 'removeLink', label: 'Remove Link', icon: 'closeWindow' },
  ],
  image: [
    { key: 'editImageUrl', label: 'Edit Image URL...', icon: 'image' },
    { key: 'deleteImage', label: 'Delete Image', icon: 'closeWindow' },
  ],
  table: [
    { key: 'insertRowAbove', label: 'Insert Row Above', icon: 'table' },
    { key: 'insertRowBelow', label: 'Insert Row Below', icon: 'table' },
    { key: 'deleteRow', label: 'Delete Row', icon: 'closeWindow' },
    { key: 'insertColumnLeft', label: 'Insert Column Left', icon: 'table' },
    { key: 'insertColumnRight', label: 'Insert Column Right', icon: 'table' },
    { key: 'deleteColumn', label: 'Delete Column', icon: 'closeWindow' },
    { key: 'mergeOrSplitCells', label: 'Merge or Split Cells', icon: 'table' },
    { key: 'headerRow', label: 'Header Row', icon: 'table' },
    { key: 'headerColumn', label: 'Header Column', icon: 'table' },
    { key: 'deleteTable', label: 'Delete Table', icon: 'closeWindow' },
  ],
  clipboard: [
    { key: 'cut', label: 'Cut', icon: 'cut' },
    { key: 'copy', label: 'Copy', icon: 'copy' },
    { key: 'paste', label: 'Paste', icon: 'paste' },
    { key: 'pastePlainText', label: 'Paste Plain Text', icon: 'exportTXT' },
  ],
  selection: [
    { key: 'bold', label: 'Bold', icon: 'bold' },
    { key: 'italic', label: 'Italic', icon: 'italic' },
    { key: 'underline', label: 'Underline', icon: 'underline' },
    { key: 'strikethrough', label: 'Strikethrough', icon: 'strikethrough' },
    { key: 'inlineCode', label: 'Inline Code', icon: 'inlineCode' },
    { key: 'clearFormatting', label: 'Clear Formatting', icon: 'fontSize' },
    { key: 'addLink', label: 'Add Link...', icon: 'link' },
    { key: 'flagLooseEnd', label: 'Flag as Loose End', icon: 'looseEnd' },
  ],
  caret: [
    { key: 'undo', label: 'Undo', icon: 'undo' },
    { key: 'redo', label: 'Redo', icon: 'redo' },
    { key: 'insertTable', label: 'Insert Table', icon: 'table' },
    { key: 'insertLink', label: 'Insert Link...', icon: 'link' },
    { key: 'insertHorizontalLine', label: 'Insert Horizontal Line', icon: 'horizontalRule' },
    { key: 'insertPageBreak', label: 'Insert Page Break', icon: 'pageBreak' },
    { key: 'insertDetails', label: 'Insert Collapsible Section', icon: 'details' },
    { key: 'flagLooseEnd', label: 'Add Loose End', icon: 'looseEnd' },
    { key: 'parkIdea', label: 'Park an Idea...', icon: 'parkIdea' },
  ],
  details: [
    { key: 'unsetDetails', label: 'Unwrap Collapsible Section', icon: 'closeWindow' },
  ],
  looseEnd: [
    { key: 'editLooseEnd', label: 'Edit Loose End...', icon: 'looseEnd' },
    { key: 'resolveLooseEnd', label: 'Resolve Loose End', icon: 'looseEnd' },
    { key: 'removeLooseEnd', label: 'Remove Loose End', icon: 'closeWindow' },
  ],
  document: [
    { key: 'selectAll', label: 'Select All', icon: 'selectAll' },
    { key: 'jumpTop', label: 'Go to Top', icon: 'jumpTop' },
    { key: 'jumpBottom', label: 'Go to Bottom', icon: 'jumpBottom' },
  ],
}

export const contextMenuContexts: { id: string; label: string }[] = [
  { id: 'spellcheck', label: 'Spelling' },
  { id: 'link', label: 'Link' },
  { id: 'image', label: 'Image' },
  { id: 'table', label: 'Table' },
  { id: 'details', label: 'Collapsible' },
  { id: 'looseEnd', label: 'Loose End' },
  { id: 'clipboard', label: 'Clipboard' },
  { id: 'selection', label: 'Selection' },
  { id: 'caret', label: 'Caret' },
  { id: 'document', label: 'Document' },
]

export const defaultContextMenuItems: Record<string, string[]> = {
  spellcheck: ['ignoreOnce', 'disableRule'],
  link: ['openLink', 'editLink', 'copyLinkAddress', 'removeLink'],
  image: ['editImageUrl', 'deleteImage'],
  table: ['insertRowAbove', 'insertRowBelow', 'deleteRow', 'insertColumnLeft', 'insertColumnRight', 'deleteColumn', 'mergeOrSplitCells', 'headerRow', 'headerColumn', 'deleteTable'],
  details: ['unsetDetails'],
  looseEnd: ['editLooseEnd', 'resolveLooseEnd', 'removeLooseEnd'],
  clipboard: ['cut', 'copy', 'paste', 'pastePlainText'],
  selection: ['bold', 'italic', 'underline', 'strikethrough', 'inlineCode', 'clearFormatting', 'addLink', 'flagLooseEnd'],
  caret: ['undo', 'redo', 'insertTable', 'insertDetails', 'insertLink', 'insertHorizontalLine', 'insertPageBreak', 'flagLooseEnd', 'parkIdea'],
  document: ['selectAll', 'jumpTop', 'jumpBottom'],
}

// The toolbar toggles are the desktop toolbars' alone (the phone's toolbars
// keep their own lists), so every device fills gaps with the desktop defaults.
const activeDefaultToolbar = defaultToolbarSettings

/** Settings of the taskbar tools and the page view, each checked by its own normalizer when it arrives from elsewhere. */
const TOOL_NORMALIZERS: Record<string, (raw: any) => any> = {
  toolPrefs: normalizeToolPrefs,
  calendarPrefs: normalizeCalendarPrefs,
  goalPrefs: normalizeGoalPrefs,
  docketPrefs: normalizeDocketPrefs,
  pageViewDisplay: normalizePageViewDisplay,
}

const defaults = {
  // Per device; see perDevice() in the store.
  desktopWidth: 65,
  desktopWidthUnit: 'ch' as 'px' | 'ch',
  mobileWidth: 65,
  mobileWidthUnit: 'ch' as 'px' | 'ch',
  desktopFontSize: 20,
  mobileFontSize: 18,
  fontFamily: "'Besley', Georgia, serif",
  installedFontIds: [] as string[],
  appFontOrder: [] as string[],
  hiddenAppFonts: [] as string[],
  showSystemFonts: true,
  lineHeight: 1.6,
  bgColor: '#f0e3c4',
  textColor: '#2b2115',
  theme: { ...DEFAULT_THEME_SETTINGS },
  markdownMirrorPath: null as string | null,
  jsonStorePath: null as string | null,
  startupBehavior: 'last-page' as 'last-page' | 'new-page',
  newPagePlaceholder: 'Start writing…',
  newPagePlaceholderEnabled: true,
  pageNamingSchemes: structuredClone(defaultPageNamingSchemes) as PageNamingScheme[],
  pageNamingAssignments: {} as Record<string, string>,
  documentTemplates: structuredClone(defaultDocumentTemplates) as DocumentTemplate[],
  referenceHighlightingEnabled: false,
  panelMode: 'edge' as 'button' | 'edge',
  hoverTopEnabled: true,
  hoverBottomEnabled: true,
  hoverLeftEnabled: true,
  hoverRightEnabled: true,
  workingFileFormat: 'json' as 'json' | 'markdown',
  cloudSyncUrl: '',
  cloudSyncApiKey: '',
  cloudSyncAutoSync: false,
  spellcheckEnabled: false,
  proofing: DEFAULT_PROOFING_SETTINGS,
  syncSettingsEnabled: true,
  keyLockPromptStyle: 'modal' as 'modal' | 'indicator',
  // The Lissajous figure the page-loading beam traces (Settings > Appearance).
  loadingFigure: DEFAULT_LOADING_FIGURE as LoadingFigureSetting,
  colorPickerMode: 'basic' as 'basic' | 'advanced',
  wordCountDisplay: 'abbreviated' as 'abbreviated' | 'exact',
  // What occupies the right edge on desktop: nothing, the formatting toolbar, or the taskbar.
  rightEdgeMode: 'toolbar' as RightEdgeMode,
  writingHabit: DEFAULT_WRITING_HABIT,
  // When a day begins and which day starts the week, for goals, streaks, and the writing log.
  goalDays: DEFAULT_GOAL_DAYS,
  // How the calendar opens and what it shows (Settings > Calendar).
  calendarPrefs: DEFAULT_CALENDAR_PREFS,
  // How goals and streaks are counted and shown, and how sprints run (Settings > Goals & Habits).
  goalPrefs: DEFAULT_GOAL_PREFS,
  // How Docket adds, finishes and shows tasks (Settings > Docket).
  docketPrefs: DEFAULT_DOCKET_PREFS,
  looseEnds: DEFAULT_LOOSE_END_SETTINGS,
  // Shared by the taskbar tools: which appear, dates and times, scope, colors.
  toolPrefs: DEFAULT_TOOL_PREFS,
  editorMode: DEFAULT_EDITOR_MODE_SETTINGS,
  // The page view's surroundings and paper on screen; never part of an export.
  pageViewDisplay: DEFAULT_PAGE_VIEW_DISPLAY,
  pageStatuses: DEFAULT_PAGE_STATUSES,
  mobileLayout: 'layout2', // 'layout2' (top bar above keyboard, bottom bar in sidebar)
  mobileTopPadding: 15, // Ergonomic top padding on mobile in vh/percent
  rightBarItems: [
    'panelToggle',
    'referenceManager',
    'findInPage',
    'undo',
    'redo',
    'blockType',
    'fontWeights',
    'italic',
    'underline',
    'strike',
    'alignLeft',
    'alignCenter',
    'alignRight',
    'bulletList',
    'orderedList',
    'link',
    'history',
    'fileMenu'
  ],
  mobileRightBarItems: [
    'findInPage',
    'undo',
    'redo',
    'bold',
    'italic',
    'underline',
    'bulletList',
    'orderedList',
    'link',
    'history',
    'fileMenu'
  ],
  rightBarFontWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000],
  selectionHighlightItems: [
    'cut',
    'copy',
    'selectAll',
    'bold',
    'italic',
    'underline'
  ],
  selectionEmptyItems: [
    'paste',
    'pastePlain',
    'undo',
    'redo',
    'selectAll',
    'blockType',
    'alignLeft',
    'alignCenter'
  ],
  selectionToolbarDisplayMode: 'icons', // Display mode for mobile context menu: 'icons' (default) | 'text'
  mobileKeyboardToolbarEnabled: true,
  mobileRightBarEnabled: true,
  mobileSelectionToolbarEnabled: true,
  radialMenuEnabled: false,
  mobileStaticTopToolbarEnabled: false,
  mobileStaticTopToolbarItems: [
    'panelToggle',
    'undo',
    'redo',
    'history',
    'fileMenu'
  ],
  // The toolbar resting on top of the on-screen keyboard, in order.
  mobileKeyboardToolbarItems: [
    'panelToggle',
    'undo',
    'redo',
    'blockType',
    'textColor',
    'highlightColor',
    'bold',
    'italic',
    'underline',
    'alignLeft',
    'alignCenter',
    'bulletList',
    'taskList',
    'link',
    'details',
    'looseEnd',
    'history',
    'fileMenu'
  ],
  // Radial menu: 5–10 icons around a center hub, one set for each case.
  radialMenuSelectionItems: [
    'cut',
    'copy',
    'bold',
    'italic',
    'lookupReference',
    'flagLooseEnd'
  ],
  radialMenuEmptyItems: [
    'paste',
    'undo',
    'redo',
    'selectAll',
    'parkIdea',
    'flagLooseEnd'
  ],
  desktopTypewriterModeEnabled: false, // Center-focus active cursor line
  mobileTypewriterModeEnabled: false,
  desktopTypewriterEyeline: 50, // Desktop active line eye-line (50% from bottom of container)
  desktopTypewriterThreshold: 2, // Desktop active line scroll tolerance in px
  mobileTypewriterEyeline: 70, // Mobile active line eye-line (70% from bottom of visible viewport)
  mobileTypewriterThreshold: 2, // Mobile active line scroll tolerance in px
  typewriterMotion: 'patient' as TypewriterMotion, // How the page moves the typing line to the eye-line
  typewriterBailEnabled: false, // A brief guide line at the eye-line after a long return
  clickToTypeEnabled: true, // Allow clicking in empty space below content to start typing there
  downArrowNewLineEnabled: true, // Pressing Down on the last line appends a new paragraph
  tableHeaderRowEnabled: true, // New tables start with a header row
  focusDimmingMode: 'off' as 'off' | 'paragraph' | 'sentence' | 'word',
  documentSearchPosition: 'below' as 'above' | 'below', // Find-in-page bar sits a third up ('below') or down ('above') the screen

  // Desktop Toolbar Dimensions
  toolbarStyle: 'guide' as ToolbarStyle,
  topBarRulerEnabled: true, // The column scale under the Carriage toolbar
  topBarThickness: 46,
  topBarIconSize: 28,
  bottomBarThickness: 46,
  bottomBarIconSize: 28,
  rightBarThickness: 46,
  rightBarIconSize: 28,

  // Mobile Toolbar Dimensions
  mobileKeyboardToolbarThickness: 46,
  mobileKeyboardToolbarIconSize: 32,
  mobileStaticTopToolbarThickness: 52,
  mobileStaticTopToolbarIconSize: 36,
  mobileRightBarThickness: 68,
  mobileRightBarIconSize: 44,
  mobileSelectionToolbarThickness: 42,
  mobileSelectionToolbarIconSize: 38,
  radialMenuRadius: 100,
  radialMenuButtonSize: 48,

  toolbar: activeDefaultToolbar,
  typography: defaultTypographySettings,
  iconStyles: defaultIconStyles,
  contextMenuEnabled: true,
  contextMenuItems: defaultContextMenuItems,
}

function deepMergeToolbar(persistedToolbar?: any) {
  const activeDefaults = defaultToolbarSettings
  if (!persistedToolbar) return JSON.parse(JSON.stringify(activeDefaults))
  return {
    top: { ...activeDefaults.top, ...(persistedToolbar.top || {}) },
    bottom: { ...activeDefaults.bottom, ...(persistedToolbar.bottom || {}) },
  }
}

function deepMergeTypography(persisted?: any) {
  if (!persisted) return { ...defaultTypographySettings }
  return { ...defaultTypographySettings, ...persisted }
}

function deepMergeIconStyles(persisted?: any) {
  const merged: Record<string, { size: number; strokeWidth: number }> = {}
  for (const key of Object.keys(defaultIconStyles)) {
    merged[key] = { ...defaultIconStyles[key], ...((persisted && persisted[key]) || {}) }
  }
  return merged
}

// Earlier default lists. A saved list that still matches one was never
// customized, so it picks up items added to the default since.
const legacyContextMenuDefaults: Record<string, string[][]> = {
  spellcheck: [['ignoreOnce']],
  document: [['selectAll']],
  table: [['insertRowAbove', 'insertRowBelow', 'deleteRow', 'insertColumnLeft', 'insertColumnRight', 'deleteColumn', 'mergeOrSplitCells', 'deleteTable']],
}

function isLegacyContextMenuDefault(ctxId: string, list: string[]): boolean {
  return (legacyContextMenuDefaults[ctxId] ?? []).some(
    (legacy) => legacy.length === list.length && legacy.every((key, i) => list[i] === key)
  )
}

function deepMergeContextMenuItems(persisted?: Record<string, string[]>) {
  const merged: Record<string, string[]> = {}
  for (const ctxId of Object.keys(defaultContextMenuItems)) {
    const validKeys = new Set(contextMenuItemCatalog[ctxId].map((o) => o.key))
    const persistedArr = persisted && Array.isArray(persisted[ctxId]) ? persisted[ctxId] : null
    const upgraded = persistedArr && isLegacyContextMenuDefault(ctxId, persistedArr)
    merged[ctxId] = persistedArr && !upgraded
    ? persistedArr.filter((k) => validKeys.has(k))
    : [...defaultContextMenuItems[ctxId]]
  }
  return merged
}

function loadPersisted(): any {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

/** A ref that reads and writes the desktop or the mobile copy, for the app in hand. */
function perDevice<T>(desktop: Ref<T>, mobile: Ref<T>): WritableComputedRef<T> {
  const active = IS_MOBILE_APP ? mobile : desktop
  return computed({
    get: () => active.value,
    set: (value: T) => { active.value = value },
  })
}

/**
 * A text width and its unit, cleaned up on load. Switching the unit converts
 * the width so the column stays about the same size.
 */
function createWidthPair(rawUnit: unknown, rawWidth: unknown) {
  let unitValue: 'px' | 'ch' = rawUnit === 'px' || rawUnit === 'ch'
    ? rawUnit
    : (typeof rawWidth === 'number' && rawWidth > 200 ? 'px' : 'ch')
  let widthValue: number
  if (typeof rawWidth !== 'number') {
    widthValue = unitValue === 'px' ? 700 : 65
  } else if (unitValue === 'ch') {
    widthValue = rawWidth > 200 || rawWidth < 20 ? 65 : rawWidth
  } else {
    widthValue = rawWidth < 200 || rawWidth > 3000 ? 700 : rawWidth
  }
  const unit = ref<'px' | 'ch'>(unitValue)
  const width = ref<number>(widthValue)

  watch(unit, (newUnit, oldUnit) => {
    if (newUnit === oldUnit) return
    if (newUnit === 'ch') {
      if (width.value > 200) {
        width.value = Math.min(150, Math.max(30, Math.round(width.value / 10)))
      } else if (width.value < 20) {
        width.value = 65
      }
    } else if (newUnit === 'px') {
      if (width.value <= 200) {
        const maxPx = typeof window !== 'undefined' ? window.innerWidth : 1600
        width.value = Math.min(maxPx, Math.max(300, Math.round(width.value * 10)))
      } else if (width.value < 200) {
        width.value = 700
      }
    }
  })
  return { unit, width }
}

/** Repairs per-key timestamps from a broken clock in place (see repairStoredHlcs). */
function repairSettingHlcs(local: Record<string, number>, synced: Record<string, number>) {
  let repaired = false
  for (const key of new Set([...Object.keys(local), ...Object.keys(synced)])) {
    const fixed = repairStoredHlcs(local[key] ?? 0, synced[key] ?? 0)
    if (!fixed) continue
    local[key] = fixed.hlc
    synced[key] = fixed.syncedHlc
    repaired = true
  }
  if (!repaired) return
  try {
    localStorage.setItem('ink-settings-local-hlc', JSON.stringify(local))
    localStorage.setItem('ink-settings-synced-hlc', JSON.stringify(synced))
  } catch { /* The repair runs again on the next launch. */ }
}

export const useSettingsStore = defineStore('settings', () => {
  const persisted = loadPersisted()
  const merged = { ...defaults, ...persisted }
  const backupSettings = ref<BackupSettings>(loadBackupSettings())
  merged.toolbar = deepMergeToolbar(persisted.toolbar)
  merged.typography = deepMergeTypography(persisted.typography)
  merged.iconStyles = deepMergeIconStyles(persisted.iconStyles)
  merged.contextMenuItems = deepMergeContextMenuItems(persisted.contextMenuItems)

  watch(backupSettings, (value) => {
    localStorage.setItem(LOCAL_BACKUP_STORAGE_KEY, JSON.stringify(value))
  }, { deep: true })

  // Text size, text width and typewriter mode are kept per device: a phone in
  // the hand and a monitor at arm's length want different values. Each has a
  // desktop and a mobile copy (both synced), and the plain name reads and
  // writes the copy for the app in hand. Older saves held one shared value,
  // which carries over as the desktop copy.
  const desktopWidthPair = createWidthPair(persisted.desktopWidthUnit ?? persisted.widthUnit, persisted.desktopWidth ?? persisted.width)
  const mobileWidthPair = createWidthPair(merged.mobileWidthUnit, merged.mobileWidth)
  const desktopWidthUnit = desktopWidthPair.unit
  const desktopWidth = desktopWidthPair.width
  const mobileWidthUnit = mobileWidthPair.unit
  const mobileWidth = mobileWidthPair.width
  const widthUnit = perDevice(desktopWidthUnit, mobileWidthUnit)
  const width = perDevice(desktopWidth, mobileWidth)

  const desktopFontSize = ref<number>(persisted.desktopFontSize ?? persisted.fontSize ?? 20)
  const mobileFontSize = ref<number>(merged.mobileFontSize ?? 18)
  const fontSize = perDevice(desktopFontSize, mobileFontSize)

  const fontFamily = ref<string>(merged.fontFamily || "'Cutive', Georgia, serif")
  const installedFontIds = ref<string[]>(
    Array.isArray(merged.installedFontIds)
      ? [...new Set<string>(merged.installedFontIds.filter((id: unknown): id is string => typeof id === 'string') as string[])]
      : [],
  )
  const appFontOrder = ref<string[]>(
    Array.isArray(merged.appFontOrder)
      ? [...new Set<string>(merged.appFontOrder.filter((name: unknown): name is string => typeof name === 'string') as string[])]
      : [],
  )
  const hiddenAppFonts = ref<string[]>(
    Array.isArray(merged.hiddenAppFonts)
      ? [...new Set<string>(merged.hiddenAppFonts.filter((name: unknown): name is string => typeof name === 'string') as string[])]
      : [],
  )
  const showSystemFonts = ref<boolean>(merged.showSystemFonts !== undefined ? !!merged.showSystemFonts : true)
  const lineHeight = ref<number>(merged.lineHeight || 1.6)
  const bgColor = ref<string>(merged.bgColor || '#1e1e1e')
  const textColor = ref<string>(merged.textColor || '#e0e0e0')
  const markdownMirrorPath = ref<string | null>(merged.markdownMirrorPath || null)
  const jsonStorePath = ref<string | null>(merged.jsonStorePath || null)
  const startupBehavior = ref<'last-page' | 'new-page'>(merged.startupBehavior || 'last-page')
  const newPagePlaceholder = ref<string>(
    merged.newPagePlaceholder !== undefined
      ? merged.newPagePlaceholder
      : (merged.newDocumentPlaceholder && !merged.newDocumentPlaceholder.includes('Welcome to Superelectric Ink')
          ? merged.newDocumentPlaceholder
          : 'Start writing…')
  )
  const newPagePlaceholderEnabled = ref<boolean>(merged.newPagePlaceholderEnabled !== undefined ? !!merged.newPagePlaceholderEnabled : true)
  const pageNamingSchemes = ref<PageNamingScheme[]>(
    Array.isArray(merged.pageNamingSchemes)
      ? merged.pageNamingSchemes.filter((item: any) => item && typeof item.id === 'string' && typeof item.name === 'string' && typeof item.template === 'string')
      : structuredClone(defaultPageNamingSchemes)
  )
  const pageNamingAssignments = ref<Record<string, string>>(
    merged.pageNamingAssignments && typeof merged.pageNamingAssignments === 'object'
      ? { ...merged.pageNamingAssignments }
      : {}
  )
  const documentTemplates = ref<DocumentTemplate[]>(normalizeDocumentTemplates(merged.documentTemplates))
  const referenceHighlightingEnabled = ref<boolean>(!!merged.referenceHighlightingEnabled)
  const panelMode = ref<'button' | 'edge'>(merged.panelMode)
  const hoverTopEnabled = ref<boolean>(merged.hoverTopEnabled !== false)
  const hoverBottomEnabled = ref<boolean>(merged.hoverBottomEnabled !== false)
  const hoverLeftEnabled = ref<boolean>(merged.hoverLeftEnabled !== false)
  const hoverRightEnabled = ref<boolean>(merged.hoverRightEnabled !== false)
  const workingFileFormat = ref<'json' | 'markdown'>(merged.workingFileFormat || 'json')
  const cloudSyncUrl = ref<string>(merged.cloudSyncUrl || '')
  const cloudSyncApiKey = ref<string>(merged.cloudSyncApiKey || '')
  const cloudSyncAutoSync = ref<boolean>(!!merged.cloudSyncAutoSync)
  const spellcheckEnabled = ref<boolean>(!!merged.spellcheckEnabled)
  const proofing = ref<ProofingSettings>(normalizeProofingSettings(merged.proofing))
  const syncSettingsEnabled = ref<boolean>(!!merged.syncSettingsEnabled)
  const keyLockPromptStyle = ref<'modal' | 'indicator'>(merged.keyLockPromptStyle || 'modal')
  const loadingFigure = ref<LoadingFigureSetting>(normalizeLoadingFigure(merged.loadingFigure))
  const colorPickerMode = ref<'basic' | 'advanced'>(merged.colorPickerMode || 'basic')
  const wordCountDisplay = ref<'abbreviated' | 'exact'>(merged.wordCountDisplay === 'exact' ? 'exact' : 'abbreviated')
  const writingHabit = ref<WritingHabit>(normalizeWritingHabit(merged.writingHabit))
  const goalDays = ref<GoalDays>(normalizeGoalDays(merged.goalDays))
  const calendarPrefs = ref<CalendarPrefs>(normalizeCalendarPrefs(persisted.calendarPrefs ?? {
    // Carry over the two calendar switches kept before the Calendar page.
    layers: { goalDots: persisted.calendarShowGoals === true, tasks: persisted.calendarShowTasks !== false },
  }))
  const goalPrefs = ref<GoalPrefs>(normalizeGoalPrefs(persisted.goalPrefs ?? {
    // The sprint length was kept on each device before Settings had a Goals page.
    sprint: { defaultMinutes: Number(localStorage.getItem('ink-sprint-minutes')) || undefined },
  }))
  const docketPrefs = ref<DocketPrefs>(normalizeDocketPrefs(persisted.docketPrefs))
  const looseEnds = ref<LooseEndSettings>(normalizeLooseEndSettings(merged.looseEnds))
  const toolPrefs = ref<ToolPrefs>(normalizeToolPrefs(merged.toolPrefs))
  const editorMode = ref<EditorModeSettings>(normalizeEditorModeSettings(merged.editorMode))
  const pageViewDisplay = ref<PageViewDisplay>(normalizePageViewDisplay(merged.pageViewDisplay))
  const pageStatuses = ref<PageStatus[]>(normalizePageStatuses(merged.pageStatuses))
  const rightEdgeMode = ref<RightEdgeMode>(
    ['none', 'toolbar', 'taskbar'].includes(merged.rightEdgeMode) ? merged.rightEdgeMode : 'toolbar',
  )
  const theme = ref<ThemeSettings>(normalizeThemeSettings(merged.theme))

  const currentLocalHlcs = ref<Record<string, number>>(
    JSON.parse(localStorage.getItem('ink-settings-local-hlc') || '{}')
  )
  const lastSyncedHlcs = ref<Record<string, number>>(
    JSON.parse(localStorage.getItem('ink-settings-synced-hlc') || '{}')
  )
  repairSettingHlcs(currentLocalHlcs.value, lastSyncedHlcs.value)
  const mobileLayout = ref<string>(merged.mobileLayout || 'layout2')
  const toolbar = ref(merged.toolbar)
  const typography = ref(merged.typography)
  const iconStyles = ref(merged.iconStyles)
  const contextMenuEnabled = ref<boolean>(merged.contextMenuEnabled !== undefined ? !!merged.contextMenuEnabled : true)
  const contextMenuItems = ref<Record<string, string[]>>(merged.contextMenuItems)
  const mobileTopPadding = ref<number>(merged.mobileTopPadding !== undefined ? merged.mobileTopPadding : 15)
  const rightBarItems = ref<string[]>(merged.rightBarItems || [])
  // Introduce the project reference manager once without continually
  // overriding a user's later decision to remove or reposition it.
  try {
    const migrationKey = 'ink-reference-manager-toolbar-v1'
    if (localStorage.getItem(migrationKey) !== 'done') {
      if (!rightBarItems.value.includes('referenceManager')) {
        const panelIndex = rightBarItems.value.indexOf('panelToggle')
        rightBarItems.value.splice(panelIndex >= 0 ? panelIndex + 1 : 0, 0, 'referenceManager')
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...persisted, rightBarItems: rightBarItems.value }))
      }
      localStorage.setItem(migrationKey, 'done')
    }
  } catch {}
  const mobileRightBarItems = ref<string[]>(merged.mobileRightBarItems || [])
  // Introduce find in page once, the same way (and for the same reason).
  try {
    const migrationKey = 'ink-find-in-page-toolbar-v1'
    if (localStorage.getItem(migrationKey) !== 'done') {
      if (!rightBarItems.value.includes('findInPage')) {
        const referenceIndex = rightBarItems.value.indexOf('referenceManager')
        rightBarItems.value.splice(referenceIndex >= 0 ? referenceIndex + 1 : 0, 0, 'findInPage')
      }
      if (!mobileRightBarItems.value.includes('findInPage')) mobileRightBarItems.value.unshift('findInPage')
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...persisted,
        rightBarItems: rightBarItems.value,
        mobileRightBarItems: mobileRightBarItems.value,
      }))
      localStorage.setItem(migrationKey, 'done')
    }
  } catch {}
  const rightBarFontWeights = ref<number[]>(
    Array.isArray(merged.rightBarFontWeights)
      ? [...new Set<number>(merged.rightBarFontWeights
          .map(Number)
          .filter((weight: number) => Number.isInteger(weight) && weight >= 1 && weight <= 1000))]
          .sort((a, b) => a - b)
      : [...defaults.rightBarFontWeights],
  )
  const selectionHighlightItems = ref<string[]>(merged.selectionHighlightItems || [])
  const selectionEmptyItems = ref<string[]>(merged.selectionEmptyItems || [])
  const selectionToolbarDisplayMode = ref<string>(merged.selectionToolbarDisplayMode || 'icons')
  const mobileKeyboardToolbarEnabled = ref<boolean>(merged.mobileKeyboardToolbarEnabled !== undefined ? !!merged.mobileKeyboardToolbarEnabled : true)
  const mobileRightBarEnabled = ref<boolean>(merged.mobileRightBarEnabled !== undefined ? !!merged.mobileRightBarEnabled : true)
  const mobileSelectionToolbarEnabled = ref<boolean>(merged.mobileSelectionToolbarEnabled !== undefined ? !!merged.mobileSelectionToolbarEnabled : true)
  const radialMenuEnabled = ref<boolean>(!!merged.radialMenuEnabled)
  const mobileStaticTopToolbarEnabled = ref<boolean>(!!merged.mobileStaticTopToolbarEnabled)
  const mobileStaticTopToolbarItems = ref<string[]>(merged.mobileStaticTopToolbarItems || [])
  const mobileKeyboardToolbarItems = ref<string[]>(merged.mobileKeyboardToolbarItems || [])
  const radialMenuSelectionItems = ref<string[]>(merged.radialMenuSelectionItems || [])
  const radialMenuEmptyItems = ref<string[]>(merged.radialMenuEmptyItems || [])
  // The selection menu and the radial menu share the long press, so only one
  // can be on at a time.
  watch(mobileSelectionToolbarEnabled, (on) => {
    if (on && radialMenuEnabled.value) radialMenuEnabled.value = false
  })
  watch(radialMenuEnabled, (on) => {
    if (on && mobileSelectionToolbarEnabled.value) mobileSelectionToolbarEnabled.value = false
  })
  const desktopTypewriterModeEnabled = ref<boolean>(!!(persisted.desktopTypewriterModeEnabled ?? persisted.typewriterModeEnabled))
  const mobileTypewriterModeEnabled = ref<boolean>(!!merged.mobileTypewriterModeEnabled)
  const typewriterModeEnabled = perDevice(desktopTypewriterModeEnabled, mobileTypewriterModeEnabled)
  const desktopTypewriterEyeline = ref<number>(merged.desktopTypewriterEyeline !== undefined ? merged.desktopTypewriterEyeline : 50)
  const desktopTypewriterThreshold = ref<number>(merged.desktopTypewriterThreshold !== undefined ? merged.desktopTypewriterThreshold : 2)
  const mobileTypewriterEyeline = ref<number>(merged.mobileTypewriterEyeline !== undefined ? merged.mobileTypewriterEyeline : 70)
  const mobileTypewriterThreshold = ref<number>(merged.mobileTypewriterThreshold !== undefined ? merged.mobileTypewriterThreshold : 2)
  const typewriterMotion = ref<TypewriterMotion>(
    TYPEWRITER_MOTIONS.includes(merged.typewriterMotion) ? merged.typewriterMotion : 'patient'
  )
  const typewriterBailEnabled = ref<boolean>(!!merged.typewriterBailEnabled)
  const clickToTypeEnabled = ref<boolean>(merged.clickToTypeEnabled !== undefined ? !!merged.clickToTypeEnabled : true)
  const downArrowNewLineEnabled = ref<boolean>(merged.downArrowNewLineEnabled !== undefined ? !!merged.downArrowNewLineEnabled : true)
  const tableHeaderRowEnabled = ref<boolean>(merged.tableHeaderRowEnabled !== undefined ? !!merged.tableHeaderRowEnabled : true)
  const focusDimmingMode = ref<'off' | 'paragraph' | 'sentence' | 'word'>(
    (merged.focusDimmingMode as 'off' | 'paragraph' | 'sentence' | 'word') || 'off'
  )
  const documentSearchPosition = ref<'above' | 'below'>(merged.documentSearchPosition === 'above' ? 'above' : 'below')

  // Desktop Toolbar Dimension Refs
  const toolbarStyle = ref<ToolbarStyle>(['guide', 'carriage', 'pill'].includes(merged.toolbarStyle) ? merged.toolbarStyle : 'guide')
  const topBarRulerEnabled = ref<boolean>(merged.topBarRulerEnabled !== false)
  const topBarThickness = ref<number>(merged.topBarThickness ?? 46)
  const topBarIconSize = ref<number>(merged.topBarIconSize ?? 28)
  const bottomBarThickness = ref<number>(merged.bottomBarThickness ?? 46)
  const bottomBarIconSize = ref<number>(merged.bottomBarIconSize ?? 28)
  const rightBarThickness = ref<number>(merged.rightBarThickness ?? 46)
  const rightBarIconSize = ref<number>(merged.rightBarIconSize ?? 28)

  // Mobile Toolbar Dimension Refs
  const mobileKeyboardToolbarThickness = ref<number>(merged.mobileKeyboardToolbarThickness ?? 46)
  const mobileKeyboardToolbarIconSize = ref<number>(merged.mobileKeyboardToolbarIconSize ?? 32)
  const mobileStaticTopToolbarThickness = ref<number>(merged.mobileStaticTopToolbarThickness ?? 52)
  const mobileStaticTopToolbarIconSize = ref<number>(merged.mobileStaticTopToolbarIconSize ?? 36)
  const mobileRightBarThickness = ref<number>(merged.mobileRightBarThickness ?? 68)
  const mobileRightBarIconSize = ref<number>(merged.mobileRightBarIconSize ?? 44)
  const mobileSelectionToolbarThickness = ref<number>(merged.mobileSelectionToolbarThickness ?? 42)
  const mobileSelectionToolbarIconSize = ref<number>(merged.mobileSelectionToolbarIconSize ?? 38)
  const radialMenuRadius = ref<number>(merged.radialMenuRadius ?? 100)
  const radialMenuButtonSize = ref<number>(merged.radialMenuButtonSize ?? 48)

  const refMap: Record<string, Ref<any>> = {
    desktopWidth,
    desktopWidthUnit,
    mobileWidth,
    mobileWidthUnit,
    desktopFontSize,
    mobileFontSize,
    fontFamily,
    installedFontIds,
    appFontOrder,
    hiddenAppFonts,
    showSystemFonts,
    lineHeight,
    bgColor,
    textColor,
    markdownMirrorPath,
    jsonStorePath,
    startupBehavior,
    newPagePlaceholder,
    newPagePlaceholderEnabled,
    pageNamingSchemes,
    pageNamingAssignments,
    documentTemplates,
    referenceHighlightingEnabled,
    panelMode,
    hoverTopEnabled,
    hoverBottomEnabled,
    hoverLeftEnabled,
    hoverRightEnabled,
    workingFileFormat,
    cloudSyncUrl,
    cloudSyncApiKey,
    cloudSyncAutoSync,
    spellcheckEnabled,
    proofing,
    syncSettingsEnabled,
    keyLockPromptStyle,
    loadingFigure,
    colorPickerMode,
    wordCountDisplay,
    rightEdgeMode,
    writingHabit,
    goalDays,
    calendarPrefs,
    goalPrefs,
    docketPrefs,
    looseEnds,
    toolPrefs,
    editorMode,
    pageViewDisplay,
    pageStatuses,
    theme,
    mobileLayout,
    toolbar,
    typography,
    iconStyles,
    mobileTopPadding,
    rightBarItems,
    mobileRightBarItems,
    rightBarFontWeights,
    selectionHighlightItems,
    selectionEmptyItems,
    selectionToolbarDisplayMode,
    mobileKeyboardToolbarEnabled,
    mobileRightBarEnabled,
    mobileSelectionToolbarEnabled,
    radialMenuEnabled,
    mobileStaticTopToolbarEnabled,
    mobileStaticTopToolbarItems,
    mobileKeyboardToolbarItems,
    radialMenuSelectionItems,
    radialMenuEmptyItems,
    desktopTypewriterModeEnabled,
    mobileTypewriterModeEnabled,
    desktopTypewriterEyeline,
    desktopTypewriterThreshold,
    mobileTypewriterEyeline,
    mobileTypewriterThreshold,
    typewriterMotion,
    typewriterBailEnabled,
    clickToTypeEnabled,
    downArrowNewLineEnabled,
    tableHeaderRowEnabled,
    focusDimmingMode,
    documentSearchPosition,
    toolbarStyle,
    topBarRulerEnabled,
    topBarThickness,
    topBarIconSize,
    bottomBarThickness,
    bottomBarIconSize,
    rightBarThickness,
    rightBarIconSize,
    mobileKeyboardToolbarThickness,
    mobileKeyboardToolbarIconSize,
    mobileStaticTopToolbarThickness,
    mobileStaticTopToolbarIconSize,
    mobileRightBarThickness,
    mobileRightBarIconSize,
    mobileSelectionToolbarThickness,
    mobileSelectionToolbarIconSize,
    radialMenuRadius,
    radialMenuButtonSize,
    contextMenuEnabled,
    contextMenuItems,
  }

  let lastValues: Record<string, any> = {}
  for (const key of Object.keys(refMap)) {
    lastValues[key] = JSON.parse(JSON.stringify(refMap[key].value))
  }

  watch(
    [
      desktopWidth,
      desktopWidthUnit,
      mobileWidth,
      mobileWidthUnit,
      desktopFontSize,
      mobileFontSize,
      fontFamily,
      installedFontIds,
      appFontOrder,
      hiddenAppFonts,
      showSystemFonts,
      lineHeight,
      bgColor,
      textColor,
      markdownMirrorPath,
      jsonStorePath,
      startupBehavior,
      newPagePlaceholder,
      newPagePlaceholderEnabled,
      pageNamingSchemes,
      pageNamingAssignments,
      documentTemplates,
      referenceHighlightingEnabled,
      panelMode,
      hoverTopEnabled,
      hoverBottomEnabled,
      hoverLeftEnabled,
      hoverRightEnabled,
      workingFileFormat,
      cloudSyncUrl,
      cloudSyncApiKey,
      cloudSyncAutoSync,
      spellcheckEnabled,
      proofing,
      syncSettingsEnabled,
      keyLockPromptStyle,
      loadingFigure,
      wordCountDisplay,
      rightEdgeMode,
      writingHabit,
      goalDays,
      calendarPrefs,
      goalPrefs,
      docketPrefs,
      looseEnds,
      toolPrefs,
    editorMode,
    pageViewDisplay,
    pageStatuses,
      theme,
      mobileLayout,
      toolbar,
      typography,
      iconStyles,
      mobileTopPadding,
      rightBarItems,
      mobileRightBarItems,
      rightBarFontWeights,
      selectionHighlightItems,
      selectionEmptyItems,
      selectionToolbarDisplayMode,
      mobileKeyboardToolbarEnabled,
      mobileRightBarEnabled,
      mobileSelectionToolbarEnabled,
      radialMenuEnabled,
      mobileStaticTopToolbarEnabled,
      mobileStaticTopToolbarItems,
      mobileKeyboardToolbarItems,
      radialMenuSelectionItems,
      radialMenuEmptyItems,
      desktopTypewriterModeEnabled,
      mobileTypewriterModeEnabled,
      desktopTypewriterEyeline,
      desktopTypewriterThreshold,
      mobileTypewriterEyeline,
      mobileTypewriterThreshold,
      typewriterMotion,
      typewriterBailEnabled,
      clickToTypeEnabled,
      downArrowNewLineEnabled,
      tableHeaderRowEnabled,
      focusDimmingMode,
      documentSearchPosition,
      toolbarStyle,
      topBarRulerEnabled,
      topBarThickness,
      topBarIconSize,
      bottomBarThickness,
      bottomBarIconSize,
      rightBarThickness,
      rightBarIconSize,
      mobileKeyboardToolbarThickness,
      mobileKeyboardToolbarIconSize,
      mobileStaticTopToolbarThickness,
      mobileStaticTopToolbarIconSize,
      mobileRightBarThickness,
      mobileRightBarIconSize,
      mobileSelectionToolbarThickness,
      mobileSelectionToolbarIconSize,
      radialMenuRadius,
      radialMenuButtonSize,
      contextMenuEnabled,
      contextMenuItems,
    ],
    () => {
      const nowHlc = localHLC.now()
      let anyChanged = false

      const newSettings: Record<string, any> = {}
      for (const [key, r] of Object.entries(refMap)) {
        newSettings[key] = r.value
      }

      if (!isApplyingRemote) {
        for (const key of Object.keys(refMap)) {
          if (JSON.stringify(newSettings[key]) !== JSON.stringify(lastValues[key])) {
            currentLocalHlcs.value[key] = nowHlc
            anyChanged = true
          }
        }
        if (anyChanged) {
          localStorage.setItem('ink-settings-local-hlc', JSON.stringify(currentLocalHlcs.value))
        }
      }

      lastValues = JSON.parse(JSON.stringify(newSettings))

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(newSettings)
      )

      // Debounce and push settings changes to Cloudflare syncEngine if sync is enabled
      if (!isApplyingRemote && syncSettingsEnabled.value) {
        if (syncDebounceTimer) clearTimeout(syncDebounceTimer)
        syncDebounceTimer = setTimeout(() => {
          const hlcTs = localHLC.now()
          const changes = Object.entries(newSettings).map(([key, value]) => ({
            key,
            value,
            hlc: currentLocalHlcs.value[key] || hlcTs,
          }))

          syncEngine.pushSettings(changes)
        }, 500)
      }
    },
    { deep: true }
  )

  let isApplyingRemote = false
  let syncDebounceTimer: any = null

  /**
   * Applies remote settings pulled from Cloudflare Worker, respecting HLC timestamps.
   */
  function applyRemoteSettings(remoteSettings: Record<string, { value: any; hlc: number }>) {
    if (!remoteSettings || typeof remoteSettings !== 'object') return
    isApplyingRemote = true

    try {
      for (const [key, item] of Object.entries(remoteSettings)) {
        if (!item || item.value === undefined || item.value === null) continue
        if (!refMap[key]) continue

        const localHlc = currentLocalHlcs.value[key] || 0
        const lastSyncedHlc = lastSyncedHlcs.value[key] || 0

        // If local has not changed since last sync, or remote is newer than local change
        if (localHlc <= lastSyncedHlc || item.hlc > localHlc) {
          if (key === 'fontFamily' && !item.value) {
            refMap[key].value = "'Cutive', Georgia, serif"
          } else if (key === 'theme') {
            refMap[key].value = normalizeThemeSettings(item.value)
          } else if (key === 'writingHabit') {
            refMap[key].value = normalizeWritingHabit(item.value)
          } else if (key === 'goalDays') {
            refMap[key].value = normalizeGoalDays(item.value)
          } else if (key === 'looseEnds') {
            refMap[key].value = normalizeLooseEndSettings(item.value)
          } else if (TOOL_NORMALIZERS[key]) {
            refMap[key].value = TOOL_NORMALIZERS[key](item.value)
          } else if (key === 'editorMode') {
            refMap[key].value = normalizeEditorModeSettings(item.value)
          } else if (key === 'pageStatuses') {
            refMap[key].value = normalizePageStatuses(item.value)
          } else if (key === 'proofing') {
            refMap[key].value = normalizeProofingSettings(item.value)
          } else if (key === 'contextMenuItems') {
            refMap[key].value = deepMergeContextMenuItems(item.value)
          } else if (key === 'documentTemplates') {
            refMap[key].value = normalizeDocumentTemplates(item.value)
          } else {
            refMap[key].value = item.value
          }
          lastSyncedHlcs.value[key] = item.hlc
          currentLocalHlcs.value[key] = item.hlc
        }
      }
      localStorage.setItem('ink-settings-local-hlc', JSON.stringify(currentLocalHlcs.value))
      localStorage.setItem('ink-settings-synced-hlc', JSON.stringify(lastSyncedHlcs.value))
    } finally {
      isApplyingRemote = false
    }
  }

  /**
   * Pulled remote settings from cloud, performs per-key LWW resolution,
   * overwriting safe local defaults while keeping and pushing back any pre-login changes.
   * Pass a pull that is already in hand to reuse it; otherwise this pulls on
   * its own. Returns whether the remote values were applied.
   */
  async function syncSettingsWithCloud(
    prePulled?: Record<string, { value: any; hlc: number }>,
  ): Promise<boolean> {
    if (!syncSettingsEnabled.value || !syncEngine.isAuthenticated()) return false

    try {
      const remoteSettings = prePulled ?? await syncEngine.pullSettings()
      if (!remoteSettings) return false

      isApplyingRemote = true
      const localWins: { key: string; value: any; hlc: number }[] = []

      for (const key of Object.keys(refMap)) {
        const remoteItem = remoteSettings[key]
        const localHlc = currentLocalHlcs.value[key] || 0
        const lastSyncedHlc = lastSyncedHlcs.value[key] || 0

        if (localHlc > lastSyncedHlc) {
          // Local wins! Prepare to push it to the cloud.
          localWins.push({
            key,
            value: refMap[key].value,
            hlc: localHlc,
          })
          lastSyncedHlcs.value[key] = localHlc // Mark local as synced
        } else if (remoteItem && remoteItem.value !== undefined && remoteItem.value !== null) {
          // Remote wins/applies!
          if (key === 'fontFamily' && !remoteItem.value) {
            refMap[key].value = "'Cutive', Georgia, serif"
          } else if (key === 'theme') {
            refMap[key].value = normalizeThemeSettings(remoteItem.value)
          } else if (key === 'writingHabit') {
            refMap[key].value = normalizeWritingHabit(remoteItem.value)
          } else if (key === 'goalDays') {
            refMap[key].value = normalizeGoalDays(remoteItem.value)
          } else if (key === 'looseEnds') {
            refMap[key].value = normalizeLooseEndSettings(remoteItem.value)
          } else if (TOOL_NORMALIZERS[key]) {
            refMap[key].value = TOOL_NORMALIZERS[key](remoteItem.value)
          } else if (key === 'editorMode') {
            refMap[key].value = normalizeEditorModeSettings(remoteItem.value)
          } else if (key === 'pageStatuses') {
            refMap[key].value = normalizePageStatuses(remoteItem.value)
          } else if (key === 'proofing') {
            refMap[key].value = normalizeProofingSettings(remoteItem.value)
          } else if (key === 'contextMenuItems') {
            refMap[key].value = deepMergeContextMenuItems(remoteItem.value)
          } else if (key === 'documentTemplates') {
            refMap[key].value = normalizeDocumentTemplates(remoteItem.value)
          } else {
            refMap[key].value = remoteItem.value
          }
          lastSyncedHlcs.value[key] = remoteItem.hlc
          currentLocalHlcs.value[key] = remoteItem.hlc
        }
      }

      localStorage.setItem('ink-settings-local-hlc', JSON.stringify(currentLocalHlcs.value))
      localStorage.setItem('ink-settings-synced-hlc', JSON.stringify(lastSyncedHlcs.value))

      if (localWins.length > 0) {
        await syncEngine.pushSettings(localWins)
      }
      return true
    } catch (e) {
      console.warn('[SettingsStore] Settings sync with cloud failed:', e)
      return false
    } finally {
      isApplyingRemote = false
    }
  }

  /** Plain snapshot of every persisted setting, keyed as in local storage. */
  function exportableSettings(): Record<string, any> {
    const values: Record<string, any> = {}
    for (const [key, r] of Object.entries(refMap)) {
      values[key] = JSON.parse(JSON.stringify(r.value))
    }
    return values
  }

  /**
   * Applies settings carried over by a corpus import. They are treated as
   * local edits: each changed key gets a fresh HLC, so the imported values
   * win over the cloud copy instead of being overwritten by it.
   */
  async function applyImportedSettings(values: Record<string, any>): Promise<void> {
    for (const [key, value] of Object.entries(values)) {
      const target = refMap[key]
      if (!target || value === undefined || value === null) continue
      if (key === 'theme') {
        target.value = normalizeThemeSettings(value)
      } else if (key === 'writingHabit') {
        target.value = normalizeWritingHabit(value)
      } else if (key === 'goalDays') {
        target.value = normalizeGoalDays(value)
      } else if (key === 'looseEnds') {
        target.value = normalizeLooseEndSettings(value)
      } else if (TOOL_NORMALIZERS[key]) {
        target.value = TOOL_NORMALIZERS[key](value)
      } else if (key === 'editorMode') {
        target.value = normalizeEditorModeSettings(value)
      } else if (key === 'pageStatuses') {
        target.value = normalizePageStatuses(value)
      } else if (key === 'proofing') {
        target.value = normalizeProofingSettings(value)
      } else if (key === 'documentTemplates') {
        target.value = normalizeDocumentTemplates(value)
      } else if (key === 'toolbar') {
        target.value = deepMergeToolbar(value)
      } else if (key === 'typography') {
        target.value = deepMergeTypography(value)
      } else if (key === 'iconStyles') {
        target.value = deepMergeIconStyles(value)
      } else if (key === 'contextMenuItems') {
        target.value = deepMergeContextMenuItems(value)
      } else {
        target.value = JSON.parse(JSON.stringify(value))
      }
    }
    // Let the persistence watcher stamp HLCs and write local storage, then
    // push immediately rather than waiting for its debounce.
    await nextTick()
    if (syncSettingsEnabled.value) {
      if (syncDebounceTimer) clearTimeout(syncDebounceTimer)
      syncDebounceTimer = null
      const hlcTs = localHLC.now()
      await syncEngine.pushSettings(Object.entries(exportableSettings()).map(([key, value]) => ({
        key,
        value,
        hlc: currentLocalHlcs.value[key] || hlcTs,
      })))
    }
  }

  function setToolbarPreset(presetName: string) {
    if (presetName === 'default') {
      toolbar.value = JSON.parse(JSON.stringify(defaultToolbarSettings))
    } else if (presetName === 'zen') {
      const allOffTop = Object.keys(defaultToolbarSettings.top).reduce((acc: Record<string, boolean>, k: string) => {
        acc[k] = ['undo', 'redo', 'fileMenu', 'windowControls'].includes(k)
        return acc
      }, {})
      const allOffBottom = Object.keys(defaultToolbarSettings.bottom).reduce((acc: Record<string, boolean>, k: string) => {
        acc[k] = ['docName', 'saveStatus'].includes(k)
        return acc
      }, {})
      toolbar.value = { top: allOffTop, bottom: allOffBottom }
    } else if (presetName === 'writer') {
      const writerTop = Object.keys(defaultToolbarSettings.top).reduce((acc: Record<string, boolean>, k: string) => {
        acc[k] = ['panelToggle', 'undo', 'redo', 'blockType', 'bold', 'italic', 'bulletList', 'history', 'fileMenu', 'windowControls'].includes(k)
        return acc
      }, {})
      const writerBottom = Object.keys(defaultToolbarSettings.bottom).reduce((acc: Record<string, boolean>, k: string) => {
        acc[k] = ['docName', 'widthSlider', 'saveStatus', 'account'].includes(k)
        return acc
      }, {})
      toolbar.value = { top: writerTop, bottom: writerBottom }
    } else if (presetName === 'formatting') {
      const fmtTop = Object.keys(defaultToolbarSettings.top).reduce((acc: Record<string, boolean>, k: string) => {
        acc[k] = !['subscript', 'superscript', 'image', 'table'].includes(k)
        return acc
      }, {})
      toolbar.value = { top: fmtTop, bottom: JSON.parse(JSON.stringify(defaultToolbarSettings.bottom)) }
    }
  }

  function setAllToolbar(bar: string, val: boolean) {
    if (bar === 'top' || bar === 'all') {
      for (const k of Object.keys(toolbar.value.top)) {
        toolbar.value.top[k] = val
      }
    }
    if (bar === 'bottom' || bar === 'all') {
      for (const k of Object.keys(toolbar.value.bottom)) {
        toolbar.value.bottom[k] = val
      }
    }
  }

  function resetToolbar() {
    toolbar.value = JSON.parse(JSON.stringify(defaultToolbarSettings))
  }

  function resetIconStyles() {
    iconStyles.value = JSON.parse(JSON.stringify(defaultIconStyles))
  }

  function resetContextMenuItems(contextId?: string) {
    if (contextId) {
      contextMenuItems.value = {
        ...contextMenuItems.value,
        [contextId]: [...(defaultContextMenuItems[contextId] || [])],
      }
    } else {
      contextMenuItems.value = JSON.parse(JSON.stringify(defaultContextMenuItems))
    }
  }

  function addPageNamingScheme(name: string, template: string, restartPerFolder = false): PageNamingScheme {
    const scheme: PageNamingScheme = {
      id: globalThis.crypto?.randomUUID?.() ?? `scheme-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim(),
      template: template.trim(),
      restartPerFolder,
    }
    pageNamingSchemes.value = [...pageNamingSchemes.value, scheme]
    return scheme
  }

  function deletePageNamingScheme(id: string) {
    pageNamingSchemes.value = pageNamingSchemes.value.filter(scheme => scheme.id !== id)
    pageNamingAssignments.value = Object.fromEntries(
      Object.entries(pageNamingAssignments.value).filter(([, schemeId]) => schemeId !== id)
    )
  }

  function setPageNamingSchemeRestart(id: string, value: boolean) {
    pageNamingSchemes.value = pageNamingSchemes.value.map(scheme =>
    scheme.id === id ? { ...scheme, restartPerFolder: value } : scheme
    )
  }

  function setContainerNamingScheme(containerId: string, schemeId: string | null) {
    const next = { ...pageNamingAssignments.value }
    if (schemeId) next[containerId] = schemeId
    else delete next[containerId]
    pageNamingAssignments.value = next
  }

  function saveDocumentTemplate(template: DocumentTemplate): DocumentTemplate | null {
    const normalized = normalizeDocumentTemplate(template)
    if (!normalized) return null
    const index = documentTemplates.value.findIndex(item => item.id === normalized.id)
    if (index === -1) documentTemplates.value = [...documentTemplates.value, normalized]
    else documentTemplates.value = documentTemplates.value.map(item => item.id === normalized.id ? normalized : item)
    return normalized
  }

  function addDocumentTemplate(source?: DocumentTemplate): DocumentTemplate {
    const template = source ? cloneDocumentTemplate(source) : createBlankDocumentTemplate()
    if (source) {
      template.id = `template_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
      template.name = `${source.name} Copy`
      template.preset = false
    }
    documentTemplates.value = [...documentTemplates.value, template]
    return template
  }

  function deleteDocumentTemplate(id: string) {
    documentTemplates.value = documentTemplates.value.filter(template => template.id !== id)
  }

  function resetDocumentTemplate(id: string): DocumentTemplate | null {
    const preset = defaultDocumentTemplates.find(template => template.id === id)
    if (!preset) return null
    return saveDocumentTemplate(cloneDocumentTemplate(preset))
  }

  function restoreMissingDocumentTemplates() {
    const existing = new Set(documentTemplates.value.map(template => template.id))
    const missing = defaultDocumentTemplates
      .filter(template => !existing.has(template.id))
      .map(cloneDocumentTemplate)
    if (missing.length) documentTemplates.value = [...documentTemplates.value, ...missing]
  }

  return {
    exportableSettings,
    applyImportedSettings,
    backupSettings,
    width,
    widthUnit,
    fontSize,
    desktopWidth,
    desktopWidthUnit,
    mobileWidth,
    mobileWidthUnit,
    desktopFontSize,
    mobileFontSize,
    fontFamily,
    installedFontIds,
    appFontOrder,
    hiddenAppFonts,
    showSystemFonts,
    lineHeight,
    bgColor,
    textColor,
    theme,
    markdownMirrorPath,
    jsonStorePath,
    startupBehavior,
    newPagePlaceholder,
    newPagePlaceholderEnabled,
    pageNamingSchemes,
    pageNamingAssignments,
    documentTemplates,
    referenceHighlightingEnabled,
    panelMode,
    hoverTopEnabled,
    hoverBottomEnabled,
    hoverLeftEnabled,
    hoverRightEnabled,
    workingFileFormat,
    cloudSyncUrl,
    cloudSyncApiKey,
    cloudSyncAutoSync,
    spellcheckEnabled,
    proofing,
    syncSettingsEnabled,
    keyLockPromptStyle,
    loadingFigure,
    colorPickerMode,
    wordCountDisplay,
    rightEdgeMode,
    writingHabit,
    goalDays,
    calendarPrefs,
    goalPrefs,
    docketPrefs,
    looseEnds,
    toolPrefs,
    editorMode,
    pageViewDisplay,
    pageStatuses,
    mobileLayout,
    toolbar,
    typography,
    iconStyles,
    mobileTopPadding,
    rightBarItems,
    mobileRightBarItems,
    rightBarFontWeights,
    selectionHighlightItems,
    selectionEmptyItems,
    selectionToolbarDisplayMode,
    mobileKeyboardToolbarEnabled,
    mobileRightBarEnabled,
    mobileSelectionToolbarEnabled,
    radialMenuEnabled,
    mobileStaticTopToolbarEnabled,
    mobileStaticTopToolbarItems,
    mobileKeyboardToolbarItems,
    radialMenuSelectionItems,
    radialMenuEmptyItems,
    typewriterModeEnabled,
    desktopTypewriterModeEnabled,
    mobileTypewriterModeEnabled,
    desktopTypewriterEyeline,
    desktopTypewriterThreshold,
    mobileTypewriterEyeline,
    mobileTypewriterThreshold,
    typewriterMotion,
    typewriterBailEnabled,
    clickToTypeEnabled,
    downArrowNewLineEnabled,
    tableHeaderRowEnabled,
    focusDimmingMode,
    documentSearchPosition,
    toolbarStyle,
    topBarRulerEnabled,
    topBarThickness,
    topBarIconSize,
    bottomBarThickness,
    bottomBarIconSize,
    rightBarThickness,
    rightBarIconSize,
    mobileKeyboardToolbarThickness,
    mobileKeyboardToolbarIconSize,
    mobileStaticTopToolbarThickness,
    mobileStaticTopToolbarIconSize,
    mobileRightBarThickness,
    mobileRightBarIconSize,
    mobileSelectionToolbarThickness,
    mobileSelectionToolbarIconSize,
    radialMenuRadius,
    radialMenuButtonSize,
    contextMenuEnabled,
    contextMenuItems,
    resetContextMenuItems,
    addPageNamingScheme,
    deletePageNamingScheme,
    setPageNamingSchemeRestart,
    setContainerNamingScheme,
    saveDocumentTemplate,
    addDocumentTemplate,
    deleteDocumentTemplate,
    resetDocumentTemplate,
    restoreMissingDocumentTemplates,
    setToolbarPreset,
    setAllToolbar,
    resetToolbar,
    resetIconStyles,
    applyRemoteSettings,
    syncSettingsWithCloud,
  }
})
